import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_URL from '../config';
import {
  getSubscriptionTier,
  mergeSubscriptionTier,
  readUserFromStorage,
} from '../utils/subscription';
import { SovrynnLogo } from './Brand';
import VisionPassportIndicator from './Documents/VisionPassportIndicator';
import '../pages/passport-page.css';
import '../App.css';

const SECTION_LABELS = {
  authority: 'Authority',
  insurance_status: 'Insurance status',
  medical_card: 'Medical card',
  vehicles_readiness: 'Vehicles readiness',
  inspection_history_summary: 'Inspection history summary',
  doc_completeness: 'Document completeness',
  audit_readiness: 'Audit readiness',
};

const EXPIRATION_OPTIONS = [
  { value: '', label: 'Never' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
];

const CompliancePassport = () => {
  const [passportData, setPassportData] = useState(null);
  const [fleetPassportData, setFleetPassportData] = useState(null);
  /** 'fleet' | 'personal' — Pro/Fleet default to fleet when API returns data */
  const [passportViewMode, setPassportViewMode] = useState('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [shareToken, setShareToken] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [fieldsVisibility, setFieldsVisibility] = useState({
    authority: true,
    insurance_status: true,
    medical_card: true,
    vehicles_readiness: true,
    inspection_history_summary: true,
    doc_completeness: true,
    audit_readiness: true,
  });
  const [shareLoading, setShareLoading] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchPassportData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      let proFleet = false;
      try {
        const pr = await axios.get(`${API_URL}/api/auth/profile`, { headers });
        proFleet = ['pro', 'fleet'].includes(
          getSubscriptionTier(mergeSubscriptionTier(pr.data)),
        );
      } catch {
        proFleet = ['pro', 'fleet'].includes(
          getSubscriptionTier(mergeSubscriptionTier(readUserFromStorage())),
        );
      }

      const fleetPromise =
        proFleet ?
          axios.get(`${API_URL}/api/passport/fleet`, { headers }).catch((e) => {
            console.error('Fleet passport:', e);
            return { data: null };
          })
        : Promise.resolve({ data: null });

      const [personalRes, fleetRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/me/compliance-passport`, { headers }),
        fleetPromise,
      ]);

      setPassportData(personalRes.data);
      const fp = fleetRes?.data;
      if (fp && fp.passportId != null) {
        setFleetPassportData(fp);
        setPassportViewMode('fleet');
      } else {
        setFleetPassportData(null);
        setPassportViewMode('personal');
      }
    } catch (err) {
      console.error('Error fetching passport:', err);
      setError('Failed to load compliance passport. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchShareSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/passport/settings`, getAuthHeaders());
      if (res.data) {
        setShareToken(res.data.shareToken);
        setIsPublic(res.data.isPublic);
        setExpiresAt(res.data.expiresAt ? res.data.expiresAt.slice(0, 10) : null);
        if (res.data.fieldsVisibility) setFieldsVisibility((v) => ({ ...v, ...res.data.fieldsVisibility }));
      }
    } catch {
      // No passport yet
    }
  }, []);

  useEffect(() => {
    fetchPassportData();
    fetchShareSettings();
  }, [fetchPassportData, fetchShareSettings]);

  const createOrGetPassport = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/passport/create`, {}, getAuthHeaders());
      setShareToken(res.data.shareToken);
      setIsPublic(res.data.isPublic);
      setExpiresAt(res.data.expiresAt ? res.data.expiresAt.slice(0, 10) : null);
      await fetchShareSettings();
      toast.success('Share link ready');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  const updatePassportSettings = async (updates) => {
    setVisibilitySaving(true);
    try {
      await axios.patch(`${API_URL}/api/passport/settings`, updates, getAuthHeaders());
      toast.success('Settings saved');
      fetchShareSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const publicUrl = shareToken ? `${window.location.origin}/p/${shareToken}` : '';

  const copyShareLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => toast.success('Link copied to clipboard')).catch(() => toast.error('Could not copy'));
  };

  const handleVisibilityChange = (key, checked) => {
    const next = { ...fieldsVisibility, [key]: checked };
    setFieldsVisibility(next);
    updatePassportSettings({ fieldsVisibility: next });
  };

  const handleExpirationChange = (days) => {
    const expiresAt = days ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) : null;
    setExpiresAt(expiresAt);
    updatePassportSettings({ expiresAt: days ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString() : null });
  };

  const handlePrint = () => window.print();
  const handleDownloadPDF = () => window.print();

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID':
        return '#10B981';
      case 'EXPIRING':
        return '#F59E0B';
      case 'EXPIRED':
        return '#EF4444';
      case 'MISSING':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10B981';
    if (score >= 75) return '#F59E0B';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const generateReferenceNumber = () => {
    if (!passportData) return '';
    const timestamp = new Date(passportData.generatedAt).getTime();
    const userInitials = passportData.user.firstName?.[0] || 'U' + passportData.user.lastName?.[0] || 'U';
    return `SP-${userInitials}-${timestamp.toString().slice(-8)}`;
  };

  if (isLoading) {
    return (
      <div className="passport-container">
        <div className="passport-loading">
          <div className="loading-spinner"></div>
          <p>Generating your Compliance Passport...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="passport-container">
        <div className="passport-error">
          <p>{error}</p>
          <button className="passport-button" onClick={fetchPassportData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!passportData) {
    return null;
  }

  const showFleetPrimary = Boolean(fleetPassportData);
  const referenceNumber = generateReferenceNumber();
  const driScore = passportData.driScore.score;
  const scoreColor = getScoreColor(driScore);
  const fleetScoreColor = fleetPassportData ? getScoreColor(fleetPassportData.fleetDriScore) : '#6B7280';
  const fleetVerifyUrl = fleetPassportData?.passportId
    ? `${window.location.origin}/passport/${fleetPassportData.passportId}`
    : window.location.href;
  const personalVerifyUrl = publicUrl || window.location.href;

  const renderFleetPassport = () => {
    if (!fleetPassportData) return null;
    const driverCompliancePct = fleetPassportData.drivers.total > 0
      ? Math.round((fleetPassportData.drivers.compliant / fleetPassportData.drivers.total) * 100)
      : 0;
    const vehicleReadinessPct = fleetPassportData.vehicles.total > 0
      ? Math.round((fleetPassportData.vehicles.auditReady / fleetPassportData.vehicles.total) * 100)
      : 0;

    return (
      <div className="passport-public">
        <div className="passport-public__inner">
          <header className="passport-public__header">
            <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
            <h1 className="passport-public__title">Compliance Passport™</h1>
            <p className="passport-public__subtitle" style={{ color: '#E09B1A', fontWeight: 700 }}>
              FLEET COMPLIANCE PASSPORT™
            </p>
          </header>

          <section className="passport-public__card passport-public__id-card" aria-label="Fleet passport identification">
            <div className="passport-public__id-label">PASSPORT ID</div>
            <div className="passport-public__id-value">{fleetPassportData.passportId}</div>
            <div className="passport-public__id-meta">Generated {formatDate(fleetPassportData.generatedAt)}</div>
            <div className="passport-public__id-meta">Expires {formatDate(fleetPassportData.expiresAt)}</div>
            <div className="passport-public__id-verify">Verify at {fleetVerifyUrl}</div>
          </section>

          <section className="passport-public__section" aria-label="Fleet DRI score">
            <h2 className="passport-public__section-title">Fleet DRI Score™</h2>
            <div className="passport-public__dri-row">
              <div className="passport-public__dri-score">
                {fleetPassportData.fleetDriScore}
                <span className="passport-public__dri-max">/100</span>
              </div>
              <div className="passport-public__badges">
                <span className="passport-public__pill" style={{ backgroundColor: fleetScoreColor }}>
                  {fleetPassportData.label || 'Needs Setup'}
                </span>
                <span
                  className="passport-public__pill passport-public__pill--outline"
                  style={{ borderColor: '#6b7280', color: '#6b7280' }}
                >
                  Live Snapshot
                </span>
              </div>
            </div>
          </section>

          <section className="passport-public__section" aria-label="Owner and carrier">
            <h2 className="passport-public__section-title">Owner / Carrier</h2>
            <ul className="passport-public__kv">
              <li><span className="passport-public__kv-label">Name</span><span className="passport-public__kv-value">{fleetPassportData.owner.name}</span></li>
              <li><span className="passport-public__kv-label">DOT</span><span className="passport-public__kv-value">{fleetPassportData.owner.dotNumber}</span></li>
              <li><span className="passport-public__kv-label">MC</span><span className="passport-public__kv-value">{fleetPassportData.owner.mcNumber}</span></li>
            </ul>
          </section>

          <section className="passport-public__section" aria-label="Fleet documents">
            <h2 className="passport-public__section-title">Documents</h2>
            <ul className="passport-public__doc-list">
              {(passportData.documents || []).map((doc) => (
                <li key={doc.name} className="passport-public__doc-row">
                  <span className="passport-public__doc-name-wrap">
                    <span className="passport-public__doc-name">{doc.name}</span>
                    {doc.status !== 'MISSING' ? <VisionPassportIndicator doc={doc} /> : null}
                  </span>
                  <span className="passport-public__doc-status" style={{ color: getStatusColor(doc.status) }}>
                    {doc.status}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="passport-public__section" aria-label="Fleet summary">
            <h2 className="passport-public__section-title">Fleet Summary</h2>
            <ul className="passport-public__kv">
              <li><span className="passport-public__kv-label">Required documents</span><span className="passport-public__kv-value">{fleetPassportData.documents.complete} / {fleetPassportData.documents.total}</span></li>
              <li><span className="passport-public__kv-label">Drivers</span><span className="passport-public__kv-value">{fleetPassportData.drivers.compliant} / {fleetPassportData.drivers.total} compliant</span></li>
              <li><span className="passport-public__kv-label">Vehicles</span><span className="passport-public__kv-value">{fleetPassportData.vehicles.auditReady} / {fleetPassportData.vehicles.total} audit ready</span></li>
            </ul>
          </section>

          <section className="passport-public__section" aria-label="Driver compliance">
            <h2 className="passport-public__section-title">Driver Compliance</h2>
            <div className="passport-public__bar-wrap">
              <div className="passport-public__bar-bg">
                <div className="passport-public__bar-fill" style={{ width: `${Math.min(100, driverCompliancePct)}%` }} />
              </div>
              <p className="passport-public__bar-caption">{driverCompliancePct}% drivers compliant</p>
            </div>
          </section>

          <section className="passport-public__section" aria-label="Vehicle readiness">
            <h2 className="passport-public__section-title">Vehicle Readiness</h2>
            <div className="passport-public__bar-wrap">
              <div className="passport-public__bar-bg">
                <div className="passport-public__bar-fill passport-public__bar-fill--amber" style={{ width: `${Math.min(100, vehicleReadinessPct)}%` }} />
              </div>
              <p className="passport-public__bar-caption">{vehicleReadinessPct}% vehicles audit ready</p>
            </div>
          </section>

          <section className="passport-public__section" aria-label="Authority">
            <h2 className="passport-public__section-title">Authority</h2>
            <ul className="passport-public__kv">
              <li><span className="passport-public__kv-label">Status</span><span className="passport-public__kv-value">{fleetPassportData.authority.status}</span></li>
              <li><span className="passport-public__kv-label">DOT</span><span className="passport-public__kv-value">{fleetPassportData.authority.dotNumber}</span></li>
            </ul>
          </section>

          <footer className="passport-public__footer">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=132x132&data=${encodeURIComponent(fleetVerifyUrl)}`}
              alt="Fleet passport verification QR code"
              width={132}
              height={132}
              style={{ borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 12 }}
            />
            <p>
              <a href="https://sovrynn.co" className="passport-public__link">
                Powered by Sovrynn — sovrynn.co
              </a>
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              <button type="button" className="passport-public__download" onClick={handlePrint}>Print</button>
              <button type="button" className="passport-public__download" onClick={handleDownloadPDF}>Download</button>
            </div>
          </footer>
        </div>
      </div>
    );
  };

  const renderPersonalPassport = () => (
    <div className="passport-public">
      <div className="passport-public__inner">
        <header className="passport-public__header">
          <SovrynnLogo size="lg" variant="on-light" className="passport-public__logo" />
          <h1 className="passport-public__title">Compliance Passport™</h1>
          <p className="passport-public__subtitle">Verified Living Compliance Record</p>
        </header>

        <section className="passport-public__card passport-public__id-card" aria-label="Passport identification">
          <div className="passport-public__id-label">PASSPORT ID</div>
          <div className="passport-public__id-value">{referenceNumber}</div>
          <div className="passport-public__id-meta">Generated {formatDate(passportData.generatedAt)}</div>
          <div className="passport-public__id-verify">Verify at {personalVerifyUrl}</div>
        </section>

        <section className="passport-public__section" aria-label="DRI Score">
          <h2 className="passport-public__section-title">DRI Score™</h2>
          <div className="passport-public__dri-row">
            <div className="passport-public__dri-score">
              {driScore}
              <span className="passport-public__dri-max">/100</span>
            </div>
            <div className="passport-public__badges">
              <span className="passport-public__pill" style={{ backgroundColor: scoreColor }}>
                {passportData.driScore.status}
              </span>
            </div>
          </div>
        </section>

        <section className="passport-public__section" aria-label="Compliance standing">
          <h2 className="passport-public__section-title">Compliance standing</h2>
          <div className="passport-public__bar-wrap">
            <div className="passport-public__bar-bg">
              <div
                className="passport-public__bar-fill"
                style={{ width: `${Math.min(100, passportData.driScore.breakdown.documents || 0)}%` }}
              />
            </div>
            <p className="passport-public__bar-caption">
              Documents {passportData.driScore.breakdown.documents}% · Vehicles {passportData.driScore.breakdown.vehicles}% · Authority {passportData.driScore.breakdown.authority}%
            </p>
          </div>
          <ul className="passport-public__doc-list">
            {(passportData.documents || []).map((doc) => (
              <li key={doc.name} className="passport-public__doc-row">
                <span className="passport-public__doc-name-wrap">
                  <span className="passport-public__doc-name">{doc.name}</span>
                  {doc.status !== 'MISSING' ? <VisionPassportIndicator doc={doc} /> : null}
                </span>
                <span className="passport-public__doc-status" style={{ color: getStatusColor(doc.status) }}>
                  {doc.status}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {passportData.authority && (
          <section className="passport-public__section" aria-label="Authority">
            <h2 className="passport-public__section-title">Authority</h2>
            <ul className="passport-public__kv">
              <li><span className="passport-public__kv-label">DOT</span><span className="passport-public__kv-value">{passportData.authority.dotNumber}</span></li>
              <li><span className="passport-public__kv-label">MC</span><span className="passport-public__kv-value">{passportData.authority.mcNumber}</span></li>
              <li><span className="passport-public__kv-label">Operating status</span><span className="passport-public__kv-value">{passportData.authority.operatingStatus}</span></li>
            </ul>
          </section>
        )}

        <section className="passport-public__section" aria-label="Fleet">
          <h2 className="passport-public__section-title">Fleet</h2>
          <p className="passport-public__fleet-summary">
            {passportData.vehicles.length} active vehicle{passportData.vehicles.length === 1 ? '' : 's'}
          </p>
          <ul className="passport-public__vehicle-list">
            {(passportData.vehicles || []).map((v, i) => (
              <li key={`${v.vin || v.plateNumber || i}`}>{v.year} {v.make} {v.model} — {v.readinessScore}/100</li>
            ))}
          </ul>
        </section>

        <footer className="passport-public__footer">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=132x132&data=${encodeURIComponent(personalVerifyUrl)}`}
            alt="Personal passport verification QR code"
            width={132}
            height={132}
            style={{ borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 12 }}
          />
          <p>
            <a href="https://sovrynn.co" className="passport-public__link">
              Powered by Sovrynn — sovrynn.co
            </a>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" className="passport-public__download" onClick={handlePrint}>Print</button>
            <button type="button" className="passport-public__download" onClick={handleDownloadPDF}>Download</button>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <div className="passport-container" style={{ paddingTop: '24px' }}>

      {/* Shareable link card */}
      <div className="passport-view-toggle no-print" style={{ maxWidth: 720, margin: '0 auto 12px', padding: '0 16px', textAlign: 'right' }}>
        <button type="button" className="passport-button" onClick={() => setShowSharePanel((prev) => !prev)} style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
          {showSharePanel ? 'Hide Share Settings' : 'Manage Share Settings'}
        </button>
      </div>
      {showSharePanel && (
        <section className="passport-share-section no-print" aria-label="Shareable link">
          <h2 className="section-title">Shareable compliance link</h2>
          {!shareToken ? (
            <div className="passport-share-create">
              <p>Create a permissioned public link so others can view your compliance status (no sensitive documents).</p>
              <button type="button" className="passport-button" onClick={createOrGetPassport} disabled={shareLoading}>
                {shareLoading ? 'Creating…' : 'Create share link'}
              </button>
            </div>
          ) : (
            <>
              <div className="passport-share-url-row">
                <input type="text" readOnly className="passport-share-url" value={publicUrl} aria-label="Share URL" />
                <button type="button" className="passport-button" onClick={copyShareLink}>Copy link</button>
              </div>
              <div className="passport-share-toggles">
                <label className="passport-share-toggle">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => {
                      setIsPublic(e.target.checked);
                      updatePassportSettings({ isPublic: e.target.checked });
                    }}
                  />
                  <span>Enable public link (anyone with the link can view)</span>
                </label>
                <div className="passport-share-expiration">
                  <label>Link expiration</label>
                  <select
                    value={expiresAt ? (() => {
                      const d = new Date(expiresAt);
                      if (Number.isNaN(d.getTime())) return '';
                      const now = new Date();
                      const days = Math.round((d - now) / (24 * 60 * 60 * 1000));
                      if (days <= 0) return '';
                      if (days <= 7) return 7;
                      if (days <= 30) return 30;
                      return '';
                    })() : ''}
                    onChange={(e) => handleExpirationChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={visibilitySaving}
                  >
                    {EXPIRATION_OPTIONS.map((opt) => (
                      <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="passport-share-visibility">
                <p className="passport-share-visibility-title">Visible sections on public page</p>
                <div className="passport-share-visibility-grid">
                  {Object.entries(SECTION_LABELS).map(([key, label]) => (
                    <label key={key} className="passport-share-visibility-item">
                      <input
                        type="checkbox"
                        checked={fieldsVisibility[key] !== false}
                        onChange={(e) => handleVisibilityChange(key, e.target.checked)}
                        disabled={visibilitySaving}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {showFleetPrimary && passportViewMode === 'fleet' && fleetPassportData ? renderFleetPassport() : null}

      {showFleetPrimary && passportViewMode === 'fleet' && fleetPassportData ?
        <div className="passport-view-toggle no-print" style={{ maxWidth: 720, margin: '16px auto 24px', padding: '0 16px' }}>
          <button type="button" className="passport-button" onClick={() => setPassportViewMode('personal')}>
            View Personal Passport →
          </button>
        </div>
      : null}

      {showFleetPrimary && passportViewMode === 'personal' ?
        <div className="passport-view-toggle no-print" style={{ maxWidth: 720, margin: '0 auto 16px', padding: '0 16px' }}>
          <button type="button" className="passport-button" onClick={() => setPassportViewMode('fleet')}>
            ← Back to Fleet Passport
          </button>
        </div>
      : null}

      {(!showFleetPrimary || passportViewMode === 'personal') ? renderPersonalPassport() : null}
    </div>
  );
};

export default CompliancePassport;

